// User detail page — subscription summary + invoices list. From here the
// admin can pay any UNPAID invoice (which also runs the subscription reset).
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Button, Chip, Stack, Divider,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
    Pagination, CircularProgress, Alert, Switch, Snackbar, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';

import axiosInstance from '../utils/axiosInstance';
import { formatCurrency, formatDate, formatDateTime, titleCase } from '../utils/format';
import PayInvoiceDialog from '../components/PayInvoiceDialog';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 25;

const statusChipColor = {
    paid: 'success', unpaid: 'warning', void: 'default',
};

const subStatusColor = {
    active: 'success', trialing: 'info', freemium: 'default',
    past_due: 'error', canceled: 'default', incomplete: 'warning',
    requires_payment_method: 'warning',
};

const UserDetail = () => {
    const { userId } = useParams();
    const navigate = useNavigate();

    const [detail, setDetail] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [invPage, setInvPage] = useState(1);
    const [invPages, setInvPages] = useState(1);
    const [invTotal, setInvTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [invLoading, setInvLoading] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState(null);
    const [toggling, setToggling] = useState(false);
    const [payInvoice, setPayInvoice] = useState(null);

    const loadDetail = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}`);
            setDetail(r.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load user.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const loadInvoices = useCallback(async () => {
        setInvLoading(true);
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}/invoices`, {
                params: { page: invPage, limit: PAGE_SIZE },
            });
            setInvoices(r.data?.invoices || []);
            setInvPages(r.data?.pages || 1);
            setInvTotal(r.data?.total || 0);
        } catch (err) {
            setSnackbar({ severity: 'error', message: 'Could not load invoices.' });
        } finally {
            setInvLoading(false);
        }
    }, [userId, invPage]);

    useEffect(() => { loadDetail(); }, [loadDetail]);
    useEffect(() => { loadInvoices(); }, [loadInvoices]);

    const toggleActive = async () => {
        if (!detail?.user) return;
        setToggling(true);
        try {
            const r = await axiosInstance.patch(`/admin/users/${detail.user.user_id}/active`, {
                is_active: !detail.user.is_active,
            });
            setDetail((d) => ({ ...d, user: { ...d.user, is_active: !d.user.is_active } }));
            setSnackbar({ severity: 'success', message: r.data?.message || 'Updated.' });
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not update user.',
            });
        } finally {
            setToggling(false);
        }
    };

    const onPaid = (data) => {
        setSnackbar({ severity: 'success', message: 'Invoice settled. Subscription reset.' });
        setPayInvoice(null);
        // Optimistic patch: if the returned data has the updated sub, refresh.
        loadDetail();
        loadInvoices();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) {
        return (
            <>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}
                        sx={{ fontFamily, textTransform: 'none', mb: 2 }}>
                    Back to users
                </Button>
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            </>
        );
    }
    if (!detail) return null;

    const { user, subscription } = detail;

    return (
        <Box sx={{ fontFamily }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}
                    sx={{ fontFamily, textTransform: 'none', mb: 2 }}>
                Back to users
            </Button>

            {/* User card */}
            <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                           alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                                {user.user_email}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                <Chip size="small" label={`#${user.user_id}`} sx={{ fontFamily }} />
                                <Chip size="small" label={user.account_type || 'individual'} sx={{ fontFamily }} />
                                {user.verified
                                    ? <Chip size="small" color="success" label="Verified" sx={{ fontFamily }} />
                                    : <Chip size="small" color="warning" label="Unverified" sx={{ fontFamily }} />}
                                {user.is_active
                                    ? <Chip size="small" color="success" label="Active" sx={{ fontFamily }} />
                                    : <Chip size="small" color="error" label="Deactivated" sx={{ fontFamily }} />}
                            </Stack>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                {user.is_active ? 'Deactivate' : 'Re-activate'}
                            </Typography>
                            <Switch checked={!!user.is_active} onChange={toggleActive} disabled={toggling} />
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* Subscription card */}
            <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
                <CardContent>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a', mb: 1 }}>
                        Current subscription
                    </Typography>
                    {subscription ? (
                        <>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                                   justifyContent="space-between" alignItems={{ sm: 'center' }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 700 }}>
                                        {subscription.plan?.display_name || '--'}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                                        {subscription.plan?.price > 0
                                            ? `${formatCurrency(subscription.plan?.price, subscription.plan?.currency)} / ${subscription.plan?.interval || 'month'}`
                                            : 'Free plan'}
                                    </Typography>
                                </Box>
                                <Chip
                                    size="small"
                                    color={subStatusColor[subscription.status] || 'default'}
                                    label={titleCase(subscription.status)}
                                    sx={{ fontFamily, fontWeight: 700 }}
                                />
                            </Stack>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                                <DetailItem label="Period start" value={formatDate(subscription.current_period_start)} />
                                <DetailItem label="Period end" value={formatDate(subscription.current_period_end)} />
                                <DetailItem label="Grace ends" value={formatDate(subscription.grace_period_ends_at)} />
                                <DetailItem label="Trial ends" value={formatDate(subscription.trial_ends_at)} />
                            </Stack>
                            {subscription.last_charge_error && (
                                <Alert severity="warning" sx={{ fontFamily, mt: 2 }}>
                                    Last charge error: {subscription.last_charge_error}
                                </Alert>
                            )}
                        </>
                    ) : (
                        <Typography sx={{ fontFamily, color: '#64748b' }}>
                            No subscription on file.
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Invoices */}
            <Card variant="outlined" sx={{ borderRadius: '14px' }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                            Invoices
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                            {invTotal} total
                        </Typography>
                    </Stack>
                    {invLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : invoices.length === 0 ? (
                        <Typography sx={{ fontFamily, color: '#64748b', py: 3, textAlign: 'center' }}>
                            No invoices yet.
                        </Typography>
                    ) : (
                        <>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
                                <Table size="small">
                                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Invoice</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Issued</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Period</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Amount</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Status</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Paid via</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoices.map((inv) => (
                                            <TableRow key={inv.id} hover>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>
                                                    {inv.invoice_number}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily }}>
                                                    {formatDate(inv.issued_at)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                    {inv.period_label || '--'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily }} align="right">
                                                    {formatCurrency(inv.total, inv.currency)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={statusChipColor[inv.status] || 'default'}
                                                        label={titleCase(inv.status)}
                                                        sx={{ fontFamily, fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                    {inv.paid_via
                                                        ? `${titleCase(inv.paid_via)}${inv.check_number ? ` #${inv.check_number}` : ''}`
                                                        : '--'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {inv.status === 'unpaid' ? (
                                                        <Button
                                                            size="small" variant="contained"
                                                            startIcon={<PaymentIcon fontSize="small" />}
                                                            onClick={() => setPayInvoice(inv)}
                                                            sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                                                        >
                                                            Pay
                                                        </Button>
                                                    ) : (
                                                        <Tooltip title={`Paid ${formatDateTime(inv.paid_at)}`}>
                                                            <span style={{ color: '#94a3b8', fontFamily, fontSize: '0.78rem' }}>
                                                                {inv.paid_at ? formatDate(inv.paid_at) : '--'}
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {invPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Pagination count={invPages} page={invPage}
                                                onChange={(_, p) => setInvPage(p)}
                                                size="small" color="primary" />
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <PayInvoiceDialog
                open={!!payInvoice} invoice={payInvoice}
                onClose={() => setPayInvoice(null)}
                onPaid={onPaid}
            />

            <Snackbar
                open={!!snackbar} autoHideDuration={4500} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ fontFamily }}>
                        {snackbar.message}
                    </Alert>
                ) : null}
            </Snackbar>
        </Box>
    );
};

const DetailItem = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5,
                          textTransform: 'uppercase', color: '#94a3b8' }}>
            {label}
        </Typography>
        <Typography sx={{ fontFamily, fontSize: '0.92rem', color: '#1e293b' }}>
            {value || '--'}
        </Typography>
    </Box>
);

export default UserDetail;
